import { useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import type { RichEditorHandle } from '@/features/editor/RichEditor.js';
import { timeAgo, formatDateTime } from '@/lib/time.js';
import { useIssue, useTransitionIssue, useAuditLog } from '@/hooks/useIssues.js';
import { PriorityIcon } from '@/components/PriorityIcon.js';
import { Avatar } from '@/components/Avatar.js';
import { FollowButton } from '@/components/FollowButton.js';
import { AttachmentList } from '@/components/AttachmentList.js';
import { CommentList } from '@/features/comments/CommentList.js';
import { CommentForm } from '@/features/comments/CommentForm.js';
import { LinkedIssues } from './LinkedIssues.js';
import { ChildIssues } from './ChildIssues.js';
import { BlockedNotice } from './BlockedNotice.js';
import { childPanelPlacement } from '@/lib/child-issues.js';
import { StatusTransitionMenu } from './StatusTransitionMenu.js';
import { IssueTypeIcon } from '@/components/IssueTypeIcon.js';
import { EditableSummary, EditablePriority, EditableAssignee } from './EditIssueFields.js';
import { EditableDescription } from './EditableDescription.js';



const AUDIT_LABELS: Record<string, string> = {
  comment_added: 'added a comment',
  comment_deleted: 'deleted a comment',
  comment_edited: 'edited a comment',
  attachment_uploaded: 'uploaded an attachment',
  attachment_deleted: 'deleted an attachment',
  issue_created: 'created this issue',
  issue_updated: 'updated fields',
  issue_transitioned: 'changed status',
  link_added: 'linked a work item',
  link_removed: 'removed a link',
  subtask_added: 'added a work item under this one',
  follow: 'started following',
  unfollow: 'stopped following',
};

function auditActionLabel(action: string): string {
  return AUDIT_LABELS[action] || action;
}

function AuditIcon({ action }: { action: string }) {
  const colors: Record<string, string> = {
    comment_added: 'text-blue-400',
    comment_deleted: 'text-red-400',
    comment_edited: 'text-blue-400',
    attachment_uploaded: 'text-green-400',
    attachment_deleted: 'text-red-400',
    issue_created: 'text-purple-400',
    issue_updated: 'text-yellow-500',
    issue_transitioned: 'text-indigo-400',
    link_added: 'text-teal-500',
    link_removed: 'text-red-400',
    follow: 'text-pink-400',
    unfollow: 'text-gray-400',
  };
  return (
    <span className={`mt-0.5 text-base leading-none ${colors[action] || 'text-gray-400'}`}>
      {action.includes('delete') || action.includes('removed') ? '\u2212' : action.includes('add') || action.includes('upload') || action.includes('create') ? '+' : '\u2022'}
    </span>
  );
}

export function IssueDetail() {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const reporterWarning = (location.state as { reporterWarning?: string } | null)?.reporterWarning;
  const { data: issue, isLoading, error } = useIssue(key || '');
  const transition = useTransitionIssue(key || '');
  const { data: auditEntries } = useAuditLog(key || '');
  const commentEditorRef = useRef<RichEditorHandle>(null);

  if (isLoading) {
    return <div className="py-12 text-center text-gray-500">Loading issue...</div>;
  }

  if (error || !issue) {
    return <div className="py-12 text-center text-red-600">Issue not found</div>;
  }

  function handleTransition(transitionId: string) {
    transition.mutate(transitionId);
  }

  const childPlacement = childPanelPlacement(issue.subtasks.length);

  return (
    // key: remount per issue so drafts (summary edit, comment box, link form) never carry over
    <div key={issue.key} className="mx-auto max-w-[1400px]">
      {reporterWarning && <p role="status" className="mb-3 text-sm text-amber-700">{reporterWarning}</p>}
      {/* Breadcrumb + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <nav className="flex min-w-0 items-center gap-1.5 text-sm text-gray-500" aria-label="Breadcrumb">
          <button type="button" onClick={() => navigate(-1)} className="rounded px-1 py-0.5 hover:bg-gray-100 hover:text-gray-800" aria-label="Back">
            &larr;
          </button>
          <Link to={`/projects/${issue.project.key}`} className="truncate hover:text-blue-700 hover:underline">
            {issue.project.name}
          </Link>
          <span className="text-gray-300">/</span>
          {issue.parent && (
            <>
              <Link to={`/issues/${issue.parent.key}`} className="hover:text-blue-700 hover:underline" title={issue.parent.summary}>
                {issue.parent.key}
              </Link>
              <span className="text-gray-300">/</span>
            </>
          )}
          <span className="inline-flex items-center gap-1.5 font-medium text-gray-700">
            <IssueTypeIcon name={issue.issuetype.name} />
            {issue.key}
          </span>
        </nav>
        <div className="flex items-center gap-2">
          <FollowButton issueKey={issue.key} />
          <a
            href={`https://weblisters.atlassian.net/browse/${issue.key}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Open in Jira
          </a>
        </div>
      </div>

      <div className="mt-3 grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* Main content */}
        <div className="min-w-0">
          {issue.status.category !== 'done' && <BlockedNotice links={issue.links} />}
          <EditableSummary issueKey={issue.key} value={issue.summary} />

          <EditableDescription
            issueKey={issue.key}
            projectKey={issue.project.key}
            descriptionAdf={issue.descriptionAdf}
            description={issue.description}
            attachments={issue.attachments}
          />

          {issue.attachments && issue.attachments.length > 0 && (
            <AttachmentList attachments={issue.attachments} issueKey={issue.key} />
          )}

          {issue.childKind && childPlacement === 'main' && (
            <ChildIssues issueKey={issue.key} projectKey={issue.project.key} kind={issue.childKind} items={issue.subtasks} placement="main" />
          )}

          {/* Activity */}
          <section className="mt-8">
            <h3 className="mb-3 text-sm font-semibold text-gray-800">Comments</h3>
            <CommentForm issueKey={issue.key} projectKey={issue.project.key} attachments={issue.attachments} editorRef={commentEditorRef} />
            <div className="mt-4">
              <CommentList
                issueKey={issue.key}
                projectKey={issue.project.key}
                attachments={issue.attachments}
                onReply={(mention) => {
                  document.getElementById('comment-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  commentEditorRef.current?.insertMention(mention);
                }}
              />
            </div>
          </section>

          <section className="mt-8">
            <h3 className="mb-2 text-sm font-semibold text-gray-800">Portal activity</h3>
            {auditEntries && auditEntries.length > 0 ? (
              <ul className="divide-y rounded-lg border bg-white">
                {auditEntries.map((entry) => (
                  <li key={entry.id} className="flex items-start gap-2 px-3 py-2 text-xs">
                    <AuditIcon action={entry.action} />
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-gray-700">{entry.userName}</span>
                      {' '}
                      <span className="text-gray-500">{auditActionLabel(entry.action)}</span>
                      {entry.detail && (
                        <span className="ml-1 text-gray-400">&mdash; {entry.detail}</span>
                      )}
                    </div>
                    <span className="shrink-0 text-gray-400">{timeAgo(entry.createdAt)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-400">No portal activity recorded yet</p>
            )}
          </section>
        </div>

        {/* Right column */}
        <aside className="order-first space-y-5 lg:order-none">
          <StatusTransitionMenu
            status={issue.status}
            transitions={issue.transitions ?? []}
            pending={transition.isPending}
            onTransition={handleTransition}
          />

          <div className="rounded-lg border bg-white">
            <h3 className="border-b px-4 py-2.5 text-sm font-semibold text-gray-800">Details</h3>
            <dl className="grid grid-cols-[110px_minmax(0,1fr)] items-center gap-x-3 gap-y-3.5 px-4 py-3.5 text-sm">
              <dt className="text-gray-500">Assignee</dt>
              <dd className="flex min-w-0 items-center gap-2">
                {issue.assignee && <Avatar name={issue.assignee.displayName} avatarUrl={issue.assignee.avatarUrl} />}
                <EditableAssignee issueKey={issue.key} field="assignee" value={issue.assignee?.displayName || ''} projectKey={issue.project.key} />
                {issue.assignee?.isPortalUser && (
                  <span className="rounded bg-green-50 px-1.5 text-[10px] font-medium text-green-700" title="No Jira account — assigned through the portal and updated on Slack">Slack</span>
                )}
              </dd>

              <dt className="text-gray-500">Reporter</dt>
              <dd className="flex min-w-0 items-center gap-2">
                {issue.reporter ? (
                  <>
                    <Avatar name={issue.reporter.displayName} avatarUrl={issue.reporter.avatarUrl} />
                    <span className="truncate">{issue.reporter.displayName}</span>
                  </>
                ) : <span className="text-gray-400">None</span>}
              </dd>

              <dt className="text-gray-500">Priority</dt>
              <dd className="flex items-center gap-1.5">
                <PriorityIcon name={issue.priority.name} iconUrl={issue.priority.iconUrl} />
                <EditablePriority issueKey={issue.key} field="priority" value={issue.priority.name} projectKey={issue.project.key} />
              </dd>

              <dt className="text-gray-500">Type</dt>
              <dd className="flex items-center gap-2">
                <IssueTypeIcon name={issue.issuetype.name} />
                {issue.issuetype.name}
              </dd>

              {issue.parent && (
                <>
                  <dt className="text-gray-500">Parent</dt>
                  <dd className="min-w-0 truncate">
                    <Link to={`/issues/${issue.parent.key}`} className="text-blue-600 hover:underline" title={issue.parent.summary}>
                      {issue.parent.key}
                    </Link>
                    <span className="ml-1.5 text-gray-600">{issue.parent.summary}</span>
                  </dd>
                </>
              )}

              <dt className="self-start text-gray-500">Labels</dt>
              <dd className="flex flex-wrap gap-1">
                {issue.labels.length > 0
                  ? issue.labels.map((l) => (
                    <span key={l} className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">{l}</span>
                  ))
                  : <span className="text-gray-400">None</span>}
              </dd>

              <dt className="text-gray-500">Project</dt>
              <dd className="min-w-0 truncate">
                <Link to={`/projects/${issue.project.key}`} className="text-blue-600 hover:underline">
                  {issue.project.name}
                </Link>
              </dd>
            </dl>
          </div>

          {issue.childKind && childPlacement === 'side' && (
            <ChildIssues issueKey={issue.key} projectKey={issue.project.key} kind={issue.childKind} items={issue.subtasks} placement="side" />
          )}

          <LinkedIssues issueKey={issue.key} links={issue.links ?? []} />

          <p className="px-1 text-xs leading-5 text-gray-500">
            <span title={formatDateTime(issue.created)}>Created {timeAgo(issue.created)}</span>
            <br />
            <span title={formatDateTime(issue.updated)}>Updated {timeAgo(issue.updated)}</span>
          </p>
        </aside>
      </div>
    </div>
  );
}
